import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Popover,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import noResultFound from '../../../assets/icons/noResultFound.png';
import { TFieldData } from '../../../../src/types/mvpCharts';
import LaunchIcon from '@mui/icons-material/Launch';
import dayjs from 'dayjs';
import { useAreaUnit } from '../../../utils/functions/countryUtils';
interface Field {
  fieldNumber: number;
  fieldDetails: {
    geographyData: { village: string };
    growerName: string;
  };
}

interface Props {
  fieldList: Field[];
  selectedField?: number;
  onSelect: (field: Field) => void;
  searchTerm: string;
  setSearchTerm: any;
  fieldData: TFieldData;
  onBack?: () => void;
}

const FieldSelector: React.FC<Props> = ({
  fieldData,
  fieldList,
  selectedField,
  onSelect,
  searchTerm,
  setSearchTerm,
  onBack,
}) => {
  const areaUnit = useAreaUnit();
  const [anchorEl, setAnchorEl] =
    useState<HTMLElement | null>(null);

  const open = Boolean(anchorEl);
  const id = open ? 'field-selector-popover' : undefined;

  const handleOpen = (
    event: React.MouseEvent<HTMLElement>
  ) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearchTerm('');
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',

        mb: 1,
      }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
        }}>
        {/* Back Arrow */}
        <IconButton onClick={onBack} size="small">
          <ArrowBackIcon />
        </IconButton>

        {/* Field Number */}
        <Typography
          variant="h6"
          sx={{
            color: 'rgba(0,0,0,0.9)',
            fontWeight: 600,
            fontSize: 15,
          }}>
          {selectedField || 'Select Field'}
        </Typography>

        <IconButton
          onClick={handleOpen}
          size="small"
          sx={{ ml: -0.5 }}>
          <ArrowDropDownIcon />
        </IconButton>
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          ml: 5,
          mt: -0.5,
        }}>
        <Typography
          sx={{
            color: 'rgba(0,0,0,0.7)',
            fontSize: 12,
            fontStyle: 'italic',
            // textDecoration: 'underline',
          }}>
          {fieldData?.fieldDetails?.geofenceArea
            ? `${fieldData?.fieldDetails?.geofenceArea?.toFixed(
                2
              )} ${areaUnit}`
            : '--'}
        </Typography>

        <Box
          sx={{
            width: 4,
            height: 4,
            borderRadius: '50%',
            bgcolor: '#9CA3AF',
          }}
        />

        <Typography
          sx={{
            color: '#5E718D',
            fontSize: 12,
            fontStyle: 'italic',
          }}>
          {fieldData?.fieldDetails?.sowingTime?.seconds > 0
            ? dayjs(
                fieldData?.fieldDetails?.sowingTime?.seconds
                  ? fieldData?.fieldDetails?.sowingTime
                      ?.seconds * 1000
                  : 0
              ).format('DD/MM/YYYY')
            : '-'}
        </Typography>
      </Box>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            width: 400,
            borderRadius: 2,
            boxShadow: 3,
            mt: 1,
            height: 350,
          },
        }}>
        <Box sx={{ p: 1 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search by field number,village or grower"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <List
            dense
            sx={{
              maxHeight: 350,
              overflowY: 'auto',
              mt: 1,
            }}>
            {fieldList?.length > 0 ? (
              fieldList.map((field: any) => (
                <ListItem
                  key={field.fieldNumber}
                  button
                  onClick={() => {
                    onSelect(field);
                    handleClose();
                  }}>
                  <ListItemText
                    primary={
                      <Typography
                        fontWeight={600}
                        fontSize={13}
                        color={'rgba(0,0,0,0.9)'}>
                        {field.fieldNumber}
                      </Typography>
                    }
                    secondary={
                      <Typography
                        fontStyle={'italic'}
                        fontSize={12}
                        color={'rgba(0,0,0,0.7)'}>
                        {
                          field.fieldDetails?.geographyData
                            ?.village
                        }{' '}
                        • {field.fieldDetails?.growerName}
                      </Typography>
                    }
                  />
                  <IconButton
                    className="arrow-btn"
                    size="small"
                    sx={{
                      color: 'rgba(0,0,0,0.5)',
                      transition: 'color 0.2s ease',
                      '&:hover': {
                        color: '#0A7C6E', // green on hover
                      },
                    }}>
                    <LaunchIcon fontSize="small" />
                  </IconButton>
                </ListItem>
              ))
            ) : (
              <div>
                <img src={noResultFound} />
              </div>
            )}
          </List>
        </Box>
      </Popover>
    </Box>
  );
};

export default FieldSelector;
